import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import api from "../lib/api";
import { Leaf, Menu, X, ChevronDown, User as UserIcon, LogOut } from "lucide-react";

// Logo defaults to the local file in /public/logos so that neither stale CMS
// values nor stale CDN URLs can override it after deploy. The admin can
// optionally override via settings.header_logo_url (only used if non-empty).
const SITE_LOGO_FALLBACK = "/logos/logo-v3.png";

const navItems = [
  { to: "/plants", label: "Wildkräuter" },
  { to: "/identify", label: "Erkennen" },
  { to: "/tours", label: "Touren" },
  { to: "/calendar", label: "Saisonkalender" },
  { to: "/magazin", label: "Magazin" },
];

const toolsItems = [
  { to: "/coach", label: "KI-Coach" },
  { to: "/recipes", label: "Rezept-Generator" },
  { to: "/compare", label: "Vergleich" },
  { to: "/quiz", label: "Quiz" },
];

const accountItems = [
  { to: "/journal", label: "Tagebuch" },
  { to: "/favorites", label: "Merkliste" },
];

const moreItems = [
  { to: "/community", label: "Community" },
  { to: "/contact", label: "Kontakt" },
  { to: "/downloads", label: "Downloads" },
  { to: "https://freespirit.clothing", label: "Freespirit Clothing", external: true },
  { to: "/impressum", label: "Impressum" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);
  const [menuTop, setMenuTop] = useState(112);
  useEffect(() => {
    if (open && headerRef.current) setMenuTop(Math.max(0, headerRef.current.getBoundingClientRect().bottom));
  }, [open]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [cmsPages, setCmsPages] = useState([]);
  const { user, logout } = useAuth() || {};
  const settings = useSettings();
  const navigate = useNavigate();
  const appName = settings?.app_name || "AdventureCouple.lu";
  const siteLogo = settings?.header_logo_url || SITE_LOGO_FALLBACK;

  useEffect(() => {
    api
      .get("/cms/pages")
      .then((r) => setCmsPages((r.data || []).filter((p) => p.show_in_nav)))
      .catch(() => setCmsPages([]));
  }, []);

  const cmsItems = cmsPages.map((p) => ({ to: `/p/${p.slug}`, label: p.nav_label || p.title }));

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 backdrop-blur-xl border-b"
      style={{ backgroundColor: "rgba(231, 222, 208, 0.92)", borderColor: "var(--wp-border)" }}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-28 md:h-32">
          {/* Logo – defaults to local /public/logos asset (so it can never break on deploy); admin can override via settings.header_logo_url */}
          <Link to="/" className="flex items-center gap-2" data-testid="site-logo">
            <img
              src={siteLogo}
              alt={`${appName} – Natur erleben. Wissen teilen. Bewusst unterwegs.`}
              className="h-20 md:h-24 lg:h-28 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:opacity-70 ${isActive ? "text-[var(--wp-primary)]" : "text-[var(--wp-text)]"}`
                }
                data-testid={`nav-${it.label.toLowerCase()}`}
              >
                {it.label}
              </NavLink>
            ))}

            {/* Werkzeuge dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setToolsOpen(true)}
              onMouseLeave={() => setToolsOpen(false)}
            >
              <button
                className="text-sm font-medium flex items-center gap-1 hover:opacity-70"
                style={{ color: "var(--wp-text)" }}
                data-testid="nav-tools"
              >
                Werkzeuge <ChevronDown className="w-4 h-4" />
              </button>
              {toolsOpen && (
                <div className="absolute right-0 top-full pt-3 w-52" data-testid="nav-tools-dropdown">
                  <div className="rounded-2xl border bg-white shadow-xl py-2" style={{ borderColor: "var(--wp-border)" }}>
                    {toolsItems.map((m) => (
                      <Link
                        key={m.to}
                        to={m.to}
                        className="block px-4 py-2 text-sm hover:bg-[var(--wp-cream)] transition-colors"
                        style={{ color: "var(--wp-text)" }}
                        data-testid={`nav-tools-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {m.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setMoreOpen(true)}
              onMouseLeave={() => setMoreOpen(false)}
            >
              <button
                className="text-sm font-medium flex items-center gap-1 hover:opacity-70"
                style={{ color: "var(--wp-text)" }}
                data-testid="nav-more"
              >
                Mehr <ChevronDown className="w-4 h-4" />
              </button>
              {moreOpen && (
                <div
                  className="absolute right-0 top-full pt-3 w-56"
                  data-testid="nav-more-dropdown"
                >
                  <div className="rounded-2xl border bg-white shadow-xl py-2" style={{ borderColor: "var(--wp-border)" }}>
                    {moreItems.map((m) => (
                      m.external ? (
                        <a
                          key={m.to}
                          href={m.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between px-4 py-2 text-sm hover:bg-[var(--wp-cream)] transition-colors"
                          style={{ color: "var(--wp-primary)", fontWeight: 500 }}
                          data-testid={`nav-more-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <span>{m.label}</span>
                          <span className="text-xs opacity-60">↗</span>
                        </a>
                      ) : (
                        <Link
                          key={m.to}
                          to={m.to}
                          className="block px-4 py-2 text-sm hover:bg-[var(--wp-cream)] transition-colors"
                          style={{ color: "var(--wp-text)" }}
                          data-testid={`nav-more-${m.label.toLowerCase()}`}
                        >
                          {m.label}
                        </Link>
                      )
                    ))}
                    {cmsItems.length > 0 && (
                      <div className="my-1 border-t" style={{ borderColor: "var(--wp-border)" }} />
                    )}
                    {cmsItems.map((m) => (
                      <Link
                        key={m.to}
                        to={m.to}
                        className="block px-4 py-2 text-sm hover:bg-[var(--wp-cream)] transition-colors"
                        style={{ color: "var(--wp-olive)" }}
                        data-testid={`nav-more-cms-${m.to}`}
                      >
                        {m.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {user ? (
              <div
                className="relative"
                onMouseEnter={() => setAccountOpen(true)}
                onMouseLeave={() => setAccountOpen(false)}
              >
                <button
                  className="flex items-center gap-1.5 text-sm font-medium hover:opacity-70"
                  style={{ color: "var(--wp-text)" }}
                  data-testid="nav-account"
                >
                  <UserIcon className="w-4 h-4" /> {user.name?.split(" ")[0]}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {accountOpen && (
                  <div className="absolute right-0 top-full pt-3 w-52" data-testid="nav-account-dropdown">
                    <div className="rounded-2xl border bg-white shadow-xl py-2" style={{ borderColor: "var(--wp-border)" }}>
                      <Link to="/profile" className="block px-4 py-2 text-sm hover:bg-[var(--wp-cream)] transition-colors" style={{ color: "var(--wp-text)" }} data-testid="nav-account-profile">
                        Mein Profil
                      </Link>
                      {accountItems.map((m) => (
                        <Link
                          key={m.to}
                          to={m.to}
                          className="block px-4 py-2 text-sm hover:bg-[var(--wp-cream)] transition-colors"
                          style={{ color: "var(--wp-text)" }}
                          data-testid={`nav-account-${m.label.toLowerCase()}`}
                        >
                          {m.label}
                        </Link>
                      ))}
                      <div className="my-1 border-t" style={{ borderColor: "var(--wp-border)" }} />
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-[var(--wp-cream)] transition-colors"
                        style={{ color: "var(--wp-text)" }}
                        data-testid="nav-logout"
                      >
                        <LogOut className="inline w-3.5 h-3.5 mr-2 opacity-70" /> Abmelden
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="btn-primary text-sm" style={{ padding: "0.5rem 1.25rem" }} data-testid="nav-login">
                {settings?.header_login_label || "Login"}
              </Link>
            )}
          </nav>

          {/* Mobile: login button + hamburger toggle */}
          <div className="flex items-center gap-2 lg:hidden">
            {user ? (
              <Link
                to="/profile"
                className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium"
                style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-primary)" }}
                data-testid="nav-mobile-profile-btn"
                title="Mein Profil"
              >
                <UserIcon className="w-4 h-4" />
                <span className="max-w-[80px] truncate">{user.name?.split(" ")[0]}</span>
              </Link>
            ) : (
              <Link
                to="/login"
                className="btn-primary text-sm"
                style={{ padding: "0.5rem 1rem" }}
                data-testid="nav-mobile-login-btn"
              >
                {settings?.header_login_label || "Login"}
              </Link>
            )}
            <button
              className="p-2 rounded-lg hover:bg-[var(--wp-cream)] transition-colors"
              onClick={() => setOpen(!open)}
              aria-label="Menü"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu (portal to document.body to escape header's backdrop-filter containing block) */}
        {open && createPortal(
          <>
            {/* Backdrop closes on tap outside */}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Menü schließen"
              className="lg:hidden fixed inset-0 z-[60] bg-black/40"
              style={{ top: menuTop }}
            />
            <div
              className="lg:hidden fixed left-0 right-0 z-[61] overflow-y-auto overscroll-contain"
              style={{
                top: menuTop,
                bottom: 0,
                backgroundColor: "rgba(231, 222, 208, 0.98)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                paddingBottom: "calc(env(safe-area-inset-bottom, 0) + 6rem)",
              }}
              data-testid="nav-mobile-menu"
            >
              <nav className="flex flex-col px-5 pt-6">
                {/* Account card */}
                {user ? (
                  <div
                    className="flex items-center justify-between px-4 py-3 mb-6 rounded-2xl shadow-sm"
                    style={{ backgroundColor: "white", border: "1px solid var(--wp-border)" }}
                  >
                    <Link to="/profile" onClick={() => setOpen(false)} className="flex items-center gap-3 min-w-0" data-testid="nav-mobile-profile-link">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-primary)" }}>
                        <UserIcon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate" style={{ color: "var(--wp-text)" }}>{user.name}</div>
                        <div className="text-[11px] opacity-60">Mein Profil ansehen</div>
                      </div>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="p-2 rounded-full hover:bg-[var(--wp-cream)] transition-colors shrink-0"
                      data-testid="nav-mobile-logout"
                      title="Abmelden"
                    >
                      <LogOut className="w-4 h-4 opacity-70" />
                    </button>
                  </div>
                ) : null}

                {/* Section: Entdecken */}
                <div className="mb-1 px-1">
                  <div className="overline text-[10px]" style={{ color: "var(--wp-medicinal)" }}>Entdecken</div>
                </div>
                <div className="flex flex-col gap-0.5 mb-5">
                  {navItems.map((it) => (
                    <Link
                      key={it.to}
                      to={it.to}
                      onClick={() => setOpen(false)}
                      className="px-4 py-3 text-[15px] font-medium rounded-xl hover:bg-white transition-colors"
                      style={{ color: "var(--wp-text)" }}
                      data-testid={`nav-mobile-${it.label.toLowerCase()}`}
                    >
                      {it.label}
                    </Link>
                  ))}
                </div>

                {/* Section: Werkzeuge */}
                <div className="mb-1 px-1">
                  <div className="overline text-[10px]" style={{ color: "var(--wp-medicinal)" }}>Werkzeuge</div>
                </div>
                <div className="flex flex-col gap-0.5 mb-5">
                  {toolsItems.map((it) => (
                    <Link
                      key={it.to}
                      to={it.to}
                      onClick={() => setOpen(false)}
                      className="px-4 py-3 text-[15px] font-medium rounded-xl hover:bg-white transition-colors"
                      style={{ color: "var(--wp-text)" }}
                      data-testid={`nav-mobile-tools-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {it.label}
                    </Link>
                  ))}
                </div>

                {/* Section: Mein Konto (nur eingeloggt) */}
                {user && (
                  <>
                    <div className="mb-1 px-1">
                      <div className="overline text-[10px]" style={{ color: "var(--wp-medicinal)" }}>Mein Konto</div>
                    </div>
                    <div className="flex flex-col gap-0.5 mb-5">
                      {accountItems.map((it) => (
                        <Link
                          key={it.to}
                          to={it.to}
                          onClick={() => setOpen(false)}
                          className="px-4 py-3 text-[15px] font-medium rounded-xl hover:bg-white transition-colors"
                          style={{ color: "var(--wp-text)" }}
                          data-testid={`nav-mobile-account-${it.label.toLowerCase()}`}
                        >
                          {it.label}
                        </Link>
                      ))}
                    </div>
                  </>
                )}

                {/* Section: Mehr */}
                <div className="mb-1 px-1">
                  <div className="overline text-[10px]" style={{ color: "var(--wp-medicinal)" }}>Mehr</div>
                </div>
                <div className="flex flex-col gap-0.5 mb-5">
                  {moreItems.map((it) => (
                    it.external ? (
                      <a
                        key={it.to}
                        href={it.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpen(false)}
                        className="flex items-center justify-between px-4 py-3 text-[15px] font-semibold rounded-xl hover:bg-white transition-colors"
                        style={{ color: "var(--wp-primary)", backgroundColor: "rgba(203, 188, 165, 0.25)" }}
                        data-testid={`nav-mobile-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span>{it.label}</span>
                        <span className="text-xs opacity-70">↗</span>
                      </a>
                    ) : (
                      <Link
                        key={it.to}
                        to={it.to}
                        onClick={() => setOpen(false)}
                        className="px-4 py-3 text-[15px] font-medium rounded-xl hover:bg-white transition-colors"
                        style={{ color: "var(--wp-text)" }}
                        data-testid={`nav-mobile-${it.label.toLowerCase()}`}
                      >
                        {it.label}
                      </Link>
                    )
                  ))}
                </div>

                {/* Section: CMS pages (if any) */}
                {cmsItems.length > 0 && (
                  <>
                    <div className="mb-1 px-1">
                      <div className="overline text-[10px]" style={{ color: "var(--wp-medicinal)" }}>Seiten</div>
                    </div>
                    <div className="flex flex-col gap-0.5 mb-5">
                      {cmsItems.map((it) => (
                        <Link
                          key={it.to}
                          to={it.to}
                          onClick={() => setOpen(false)}
                          className="px-4 py-3 text-[15px] font-medium rounded-xl hover:bg-white transition-colors"
                          style={{ color: "var(--wp-olive)" }}
                          data-testid={`nav-mobile-cms-${it.to}`}
                        >
                          {it.label}
                        </Link>
                      ))}
                    </div>
                  </>
                )}

                {/* Register CTA for logged-out users */}
                {!user && (
                  <Link
                    to="/register"
                    onClick={() => setOpen(false)}
                    className="mt-2 px-4 py-3 text-sm text-center rounded-xl border-2 border-dashed hover:bg-white transition-colors"
                    style={{ borderColor: "var(--wp-border)", color: "var(--wp-primary)" }}
                    data-testid="nav-mobile-register"
                  >
                    Noch kein Konto? <span className="font-semibold underline">Jetzt registrieren</span>
                  </Link>
                )}
              </nav>
            </div>
          </>,
          document.body
        )}
      </div>
    </header>
  );
}
